/**
 * WebSocket Service for ilovemacumba-app
 *
 * Provides WebSocket connectivity with:
 * - Automatic reconnection with exponential backoff
 * - Heartbeat monitoring (ping/pong)
 * - Message queuing for offline support
 * - Event-based message handling
 * - Connection state management
 */

import { resolveWebSocketScope } from "../utils/websocketScope";
import { API_BASE_URL } from "./axiosClient";
import type {
  WSConfig,
  WSConnectionState,
  WSEventListener,
  WSMessage,
  WSMessageType,
  IWSService,
  CalendarRealtimeEnvelope,
  GamificationBadgeEarnedPayload,
  GamificationPointsUpdatedPayload,
  GamificationStreakUpdatedPayload,
  GamificationMilestoneReachedPayload,
} from "../types/websocket.types";

// Re-export types for convenience
export { WSMessageType, WSConnectionState } from "../types/websocket.types";
export type {
  WSConfig,
  WSMessage,
  WSEventListener,
  CalendarRealtimeEnvelope,
  GamificationPointsUpdatedPayload,
  GamificationBadgeEarnedPayload,
  GamificationStreakUpdatedPayload,
  GamificationMilestoneReachedPayload,
  RequestCreatedPayload,
  RequestUpdatedPayload,
  RequestApprovedPayload,
  RequestRejectedPayload,
  RequestCancelledPayload,
  ConnectedPayload,
  ErrorPayload,
} from "../types/websocket.types";

/**
 * Default WebSocket configuration
 */
const DEFAULT_CONFIG: Required<
  Omit<WSConfig, "terreiroId" | "mediumId" | "scope">
> = {
  url: getDefaultWSUrl(),
  autoReconnect: true,
  maxReconnectAttempts: 10,
  reconnectDelay: 1000,
  maxReconnectDelay: 30000,
  pingInterval: 25000,
  pongTimeout: 10000,
};

/**
 * Get the default WebSocket URL from the API base URL
 */
function getDefaultWSUrl(): string {
  if (API_BASE_URL.startsWith("/")) {
    if (typeof window !== "undefined") {
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      return `${protocol}//${window.location.host}`;
    }
    return "ws://localhost:5173";
  }

  try {
    const parsed = new URL(API_BASE_URL);
    const segments = parsed.pathname.split("/").filter(Boolean);

    if (segments.at(-1) === "v1") {
      segments.pop();
    }

    parsed.pathname = segments.length ? `/${segments.join("/")}` : "";

    return parsed.toString().replace(/^http/, "ws").replace(/\/$/, "");
  } catch {
    const baseUrl = API_BASE_URL.replace(/\/v1\/?$/, "");
    return baseUrl.replace(/^http/, "ws");
  }
}

/**
 * WebSocket Service class
 *
 * Singleton pattern - use wsService export for global access
 */
class WebSocketService implements IWSService {
  private socket: WebSocket | null = null;
  private config: Required<
    Omit<WSConfig, "terreiroId" | "mediumId" | "scope">
  > & {
    terreiroId?: string;
    mediumId?: string;
    scope?: "social";
  } = {
    ...DEFAULT_CONFIG,
  };
  private _state: WSConnectionState = "DISCONNECTED" as WSConnectionState;
  private reconnectAttempts = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private pongTimeout: ReturnType<typeof setTimeout> | null = null;
  private lastPongTime = 0;

  // Event listeners
  private listeners = new Map<WSMessageType | "*", Set<WSEventListener>>();

  // Message queue for offline support
  private messageQueue: WSMessage[] = [];
  private maxQueueSize = 100;

  // State change listeners (event-driven, replaces 500ms polling)
  private stateChangeListeners = new Set<(state: WSConnectionState) => void>();

  /**
   * Current connection state
   */
  get state(): WSConnectionState {
    return this._state;
  }

  private setState(newState: WSConnectionState): void {
    const oldState = this._state;
    this._state = newState;

    if (oldState !== newState) {
      console.log(`[WS] State changed: ${oldState} -> ${newState}`);
      // Notify all state change listeners
      this.stateChangeListeners.forEach((cb) => {
        try {
          cb(newState);
        } catch (err) {
          console.error("[WS] Error in state change listener:", err);
        }
      });
    }
  }

  /**
   * Subscribe to connection state changes. Returns an unsubscribe function.
   * Replaces the 500ms polling pattern (GAP-WS-6 fix).
   */
  onStateChange(callback: (state: WSConnectionState) => void): () => void {
    this.stateChangeListeners.add(callback);
    return () => {
      this.stateChangeListeners.delete(callback);
    };
  }

  /**
   * Connect to the WebSocket server
   */
  connect(config?: WSConfig): void {
    // Merge config
    if (config) {
      this.config = {
        ...DEFAULT_CONFIG,
        ...config,
        url: config.url || getDefaultWSUrl(),
      };
    }

    // Don't connect if already connected, connecting, or reconnecting
    if (
      this._state === ("CONNECTED" as WSConnectionState) ||
      this._state === ("CONNECTING" as WSConnectionState) ||
      this._state === ("RECONNECTING" as WSConnectionState)
    ) {
      console.log("[WS] Already connected or connecting");
      return;
    }

    // Cancel any pending reconnect timer
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    // Close any lingering socket before opening a new one
    if (this.socket) {
      try {
        this.socket.onclose = null;
        this.socket.onerror = null;
        this.socket.onmessage = null;
        this.socket.onopen = null;
        this.socket.close(1000, "Replacing connection");
      } catch {
        // ignore close errors on stale sockets
      }
      this.socket = null;
    }

    this.setState("CONNECTING" as WSConnectionState);

    try {
      let wsUrl = this.config.url + "/ws";
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(
        resolveWebSocketScope(this.config),
      )) {
        if (value) params.set(key, value);
      }
      const query = params.toString();
      if (query) {
        wsUrl += `?${query}`;
      }

      console.log(`[WS] Connecting to ${wsUrl}`);

      this.socket = new WebSocket(wsUrl);

      this.socket.onopen = this.handleOpen.bind(this);
      this.socket.onmessage = this.handleMessage.bind(this);
      this.socket.onclose = this.handleClose.bind(this);
      this.socket.onerror = this.handleError.bind(this);
    } catch (error) {
      console.error("[WS] Connection error:", error);
      this.setState("ERROR" as WSConnectionState);
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect(): void {
    console.log("[WS] Disconnecting...");

    // Clear reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    // Clear ping interval
    this.stopHeartbeat();

    // Close socket
    if (this.socket) {
      // Disable auto-reconnect for manual disconnect
      const prevAutoReconnect = this.config.autoReconnect;
      this.config.autoReconnect = false;

      this.socket.close(1000, "Client disconnect");
      this.socket = null;

      this.config.autoReconnect = prevAutoReconnect;
    }

    this.setState("DISCONNECTED" as WSConnectionState);
    this.reconnectAttempts = 0;
  }

  /**
   * Send a message to the server
   * Returns true if sent immediately, false if queued
   */
  send<T>(message: WSMessage<T>): boolean {
    const messageWithTimestamp = {
      ...message,
      timestamp: message.timestamp || new Date().toISOString(),
    };

    if (this.isConnected() && this.socket) {
      try {
        this.socket.send(JSON.stringify(messageWithTimestamp));
        return true;
      } catch (error) {
        console.error("[WS] Send error:", error);
        this.queueMessage(messageWithTimestamp as WSMessage);
        return false;
      }
    }

    // Queue message for later
    this.queueMessage(messageWithTimestamp as WSMessage);
    return false;
  }

  /**
   * Subscribe to a specific message type
   * Returns an unsubscribe function
   */
  on<T>(type: WSMessageType, listener: WSEventListener<T>): () => void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }

    this.listeners.get(type)!.add(listener as WSEventListener);

    // Return unsubscribe function
    return () => this.off(type, listener);
  }

  /**
   * Unsubscribe from a specific message type
   */
  off<T>(type: WSMessageType, listener: WSEventListener<T>): void {
    const typeListeners = this.listeners.get(type);
    if (typeListeners) {
      typeListeners.delete(listener as WSEventListener);
    }
  }

  /**
   * Subscribe to all messages
   * Returns an unsubscribe function
   */
  onAny(listener: WSEventListener): () => void {
    const anyKey = "*" as WSMessageType;
    if (!this.listeners.has(anyKey)) {
      this.listeners.set(anyKey, new Set());
    }

    this.listeners.get(anyKey)!.add(listener);

    return () => {
      const anyListeners = this.listeners.get(anyKey);
      if (anyListeners) {
        anyListeners.delete(listener);
      }
    };
  }

  /**
   * Subscribe to all calendar realtime messages (event/activity upsert/delete)
   */
  onCalendar(listener: WSEventListener<CalendarRealtimeEnvelope>): () => void {
    const unsubscribers = [
      this.on("calendar.event.upsert" as WSMessageType, listener),
      this.on("calendar.event.delete" as WSMessageType, listener),
      this.on("calendar.activity.upsert" as WSMessageType, listener),
      this.on("calendar.activity.delete" as WSMessageType, listener),
      this.on("calendar.ritual_bath.upsert" as WSMessageType, listener),
      this.on("calendar.ritual_bath.delete" as WSMessageType, listener),
    ];

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }

  /**
   * Subscribe to gamification points update messages
   */
  onGamificationPointsUpdated(
    listener: WSEventListener<GamificationPointsUpdatedPayload>
  ): () => void {
    return this.on<GamificationPointsUpdatedPayload>(
      "gamification.points.updated" as WSMessageType,
      listener
    );
  }

  /**
   * Subscribe to gamification badge earned messages
   */
  onGamificationBadgeEarned(listener: WSEventListener): () => void {
    return this.on<GamificationBadgeEarnedPayload>(
      "gamification.badge.earned" as WSMessageType,
      listener
    );
  }

  /**
   * Subscribe to gamification streak updated messages
   */
  onGamificationStreakUpdated(listener: WSEventListener): () => void {
    return this.on<GamificationStreakUpdatedPayload>(
      "gamification.streak.updated" as WSMessageType,
      listener
    );
  }

  /**
   * Subscribe to gamification milestone reached messages
   */
  onGamificationMilestoneReached(listener: WSEventListener): () => void {
    return this.on<GamificationMilestoneReachedPayload>(
      "gamification.milestone.reached" as WSMessageType,
      listener
    );
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return (
      this._state === ("CONNECTED" as WSConnectionState) &&
      this.socket?.readyState === WebSocket.OPEN
    );
  }

  /**
   * Get connection statistics
   */
  getStats() {
    return {
      state: this._state,
      reconnectAttempts: this.reconnectAttempts,
      queuedMessages: this.messageQueue.length,
      lastPongTime: this.lastPongTime
        ? new Date(this.lastPongTime).toISOString()
        : null,
    };
  }

  // ============= Private Methods =============

  private handleOpen(): void {
    console.log("[WS] Connected");
    this.setState("CONNECTED" as WSConnectionState);
    this.reconnectAttempts = 0;

    // Start heartbeat
    this.startHeartbeat();

    // Flush queued messages
    this.flushMessageQueue();
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const message = JSON.parse(event.data) as WSMessage;

      // Handle pong
      if (message.type === ("PONG" as WSMessageType)) {
        this.handlePong();
        return;
      }

      // Emit to specific type listeners
      const typeListeners = this.listeners.get(message.type as WSMessageType);
      if (typeListeners) {
        typeListeners.forEach((listener) => {
          try {
            listener(message);
          } catch (error) {
            console.error("[WS] Listener error:", error);
          }
        });
      }

      // Emit to "any" listeners
      const anyListeners = this.listeners.get("*" as WSMessageType);
      if (anyListeners) {
        anyListeners.forEach((listener) => {
          try {
            listener(message);
          } catch (error) {
            console.error("[WS] Any listener error:", error);
          }
        });
      }
    } catch (error) {
      console.error("[WS] Message parse error:", error);
    }
  }

  private handleClose(event: CloseEvent): void {
    console.log(
      `[WS] Connection closed: code=${event.code}, reason=${event.reason}`
    );

    this.stopHeartbeat();
    this.socket = null;

    // Don't reconnect on:
    //  - 1000: clean close (manual disconnect)
    //  - 4000: connection limit reached (reconnecting would re-trigger the limit)
    const isRecoverable =
      this.config.autoReconnect && event.code !== 1000 && event.code !== 4000;

    if (isRecoverable) {
      this.setState("RECONNECTING" as WSConnectionState);
      this.scheduleReconnect();
    } else {
      if (event.code === 4000) {
        console.warn(
          "[WS] Connection limit reached — not reconnecting. Close other tabs or wait."
        );
      }
      this.setState("DISCONNECTED" as WSConnectionState);
      this.reconnectAttempts = 0;
    }
  }

  private handleError(event: Event): void {
    console.error("[WS] WebSocket error:", event);
    this.setState("ERROR" as WSConnectionState);
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
      console.log("[WS] Max reconnect attempts reached");
      this.setState("DISCONNECTED" as WSConnectionState);
      return;
    }

    // Exponential backoff with jitter
    const delay = Math.min(
      this.config.reconnectDelay * Math.pow(2, this.reconnectAttempts) +
        Math.random() * 1000,
      this.config.maxReconnectDelay
    );

    this.reconnectAttempts++;
    console.log(
      `[WS] Scheduling reconnect attempt ${this.reconnectAttempts}/${
        this.config.maxReconnectAttempts
      } in ${Math.round(delay)}ms`
    );

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.connect();
    }, delay);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();

    this.pingInterval = setInterval(() => {
      if (this.isConnected()) {
        this.sendPing();
      }
    }, this.config.pingInterval);
  }

  private stopHeartbeat(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  private sendPing(): void {
    const pingMessage: WSMessage = {
      type: "PING" as WSMessageType,
      timestamp: new Date().toISOString(),
    };

    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(pingMessage));

      // Set pong timeout
      this.pongTimeout = setTimeout(() => {
        console.log("[WS] Pong timeout - connection may be dead");
        // Force reconnect
        this.socket?.close(4000, "Pong timeout");
      }, this.config.pongTimeout);
    }
  }

  private handlePong(): void {
    this.lastPongTime = Date.now();

    // Clear pong timeout
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  private queueMessage(message: WSMessage): void {
    // Add to queue
    this.messageQueue.push(message);

    // Trim queue if too large
    if (this.messageQueue.length > this.maxQueueSize) {
      this.messageQueue = this.messageQueue.slice(-this.maxQueueSize);
    }
  }

  private flushMessageQueue(): void {
    if (this.messageQueue.length === 0) return;

    console.log(`[WS] Flushing ${this.messageQueue.length} queued messages`);

    const queue = [...this.messageQueue];
    this.messageQueue = [];

    queue.forEach((message) => {
      this.send(message);
    });
  }
}

/**
 * Singleton WebSocket service instance
 */
export const wsService = new WebSocketService();

/**
 * Convenience function to get the WebSocket service
 */
export function getWSService(): WebSocketService {
  return wsService;
}

// Vite HMR cleanup: close orphaned sockets when the module is hot-replaced.
// Without this, each HMR update creates a new singleton while the old socket
// stays open on the server, rapidly filling the per-user connection limit.
const maybeImportMeta = (
  globalThis as {
    import?: { meta?: { hot?: { dispose: (cb: () => void) => void } } };
  }
).import?.meta;
const maybeHot = maybeImportMeta?.hot;

if (process.env.NODE_ENV !== "test" && maybeHot) {
  maybeHot.dispose(() => {
    wsService.disconnect();
  });
}
